import {
    AutorenewOutlined,
    CampaignOutlined,
    ChevronRightOutlined,
    EditNoteOutlined,
    HubOutlined,
    InfoOutlined,
    LabelOutlined,
    WebOutlined,
    LocalShippingOutlined,
    SellOutlined,
    StorefrontOutlined,
    SupportAgentOutlined,
    SupportOutlined,
} from "@mui/icons-material";
import { Box, ButtonBase, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { SIDEBAR_MOBILE_BAR_HEIGHT } from "../layout/RoomSidebar";
import { chrome, colors, typeScale } from "../theme/tokens";
import { FeatureSectionRows } from "../widgets/FeatureRows";
import { Offices } from "../widgets/Offices";
import { WidgetRenderer } from "../widgets/registry";

import type { Widget, WidgetOfType } from "../schema/widgets";
import type { PageTemplateProps } from "./types";
import type { ReactNode } from "react";

// Capabilities: a second navigation column that slides out of the room
// drawer, and one panel at a time beside it (Bill, 2026-09-08; the fifth
// build of this page since 2026-09-07). The column sits flush against the
// drawer with no gap, white like the drawer with the same right border, the
// full height of the window and sticky, and eases in from the drawer's edge
// on mount, so the two read as one nav in two levels. The page title heads
// the column (the page's nav label, "Capabilities"); the rows are a flat list: "Who we are" (intro, the About copy,
// the stat band, the marketplace strip and the leadership team in one panel)
// then the eight website sections. Row type is the drawer's (13px / 20px,
// 500, `chrome` ink colours and surfaces) so the two levels read as one nav. Rows are buttons: icon, label, a right chevron, hairline
// dividers, light gray on hover and when selected. No group headings, no
// "next" links. Picking a row writes `#capabilities-<id>` (replaceState) and
// a hash on load opens that panel; the page scrolls up only if the panel top
// is above the window. Every panel opens with its title at the page-H1 size
// and the supplier logo on the right; only the open panel's scenes mount. On
// a phone the column is one horizontal scrollable line under the top bar.
//
// The room drawer itself stays one item per page: parent nodes there fold,
// they do not navigate, and at 68px they cannot show nine anchors.
const WHO = "who-we-are";
const RAIL_WIDTH = 256;

type Section = WidgetOfType<"feature_rows">["props"]["sections"][number];
type Entry = { id: string; label: string; icon?: string };

export function CapabilitiesOverview({ page, room }: PageTemplateProps) {
    const widgets: Widget[] = page.widgets;
    const about = widgets.find((w) => w.type === "rich_text" && w.id === "who-we-are");
    const stats = widgets.find((w) => w.type === "stat_band");
    const grid = widgets.find((w) => w.type === "marketplace_grid");
    const team = widgets.find((w) => w.type === "team_grid");
    const tour = widgets.find((w): w is WidgetOfType<"feature_rows"> => w.type === "feature_rows");
    const sections: Section[] = useMemo(() => tour?.props.sections ?? [], [tour]);
    const rest = widgets.filter((w) => w !== about && w !== stats && w !== grid && w !== team && w !== tour);
    const intro = page.data?.intro as string | undefined;
    const { logoUrl, supplierName } = room.supplier;

    const entries: Entry[] = useMemo(
        () => [
            { id: WHO, label: "Who we are", icon: "about" },
            ...sections.map((s) => ({ id: s.id, label: s.title ?? s.id, icon: s.icon })),
        ],
        [sections],
    );

    const fromHash = () => {
        const hash = window.location.hash.replace(/^#capabilities-/, "");
        return entries.some((e) => e.id === hash) ? hash : entries[0].id;
    };
    const [active, setActive] = useState<string>(fromHash);

    useEffect(() => {
        const onHash = () => setActive(fromHash());
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries]);

    const select = (id: string) => {
        setActive(id);
        window.history.replaceState(null, "", `#capabilities-${id}`);
        const top = document.getElementById("capabilities-panel")?.getBoundingClientRect().top ?? 0;
        if (top < 0) window.scrollTo({ top: window.scrollY + top - 24, behavior: "smooth" });
    };

    const current = entries.find((e) => e.id === active) ?? entries[0];
    const section = sections.find((s) => s.id === current.id);

    const logo = logoUrl && (
        <Box
            component="img"
            src={logoUrl}
            alt={supplierName}
            sx={{
                height: { xs: 40, md: 56 },
                maxWidth: { xs: 140, md: 200 },
                objectFit: "contain",
                flexShrink: 0,
                mt: 0.5,
                // Above the title on a phone (see PanelHeader).
                order: { xs: -1, sm: 0 },
            }}
        />
    );

    let panel: ReactNode = null;
    if (current.id === WHO) {
        panel = (
            <>
                <PanelHeader title="Who we are" intro={intro} logo={logo} />
                <Stack spacing={5}>
                    {about && (
                        // The About paragraphs at the row-body size (the brand lead) so
                        // every body line on the panel reads the same (Bill, 2026-09-08).
                        <Box sx={{ "& p": { ...typeScale.lead, color: "text.secondary" }, maxWidth: 1064 }}>
                            <WidgetRenderer widget={about} room={room} />
                        </Box>
                    )}
                    {stats && <WidgetRenderer widget={stats} room={room} />}
                    {grid && <WidgetRenderer widget={grid} room={room} />}
                    {team && <WidgetRenderer widget={team} room={room} />}
                    <Offices />
                </Stack>
            </>
        );
    } else if (section) {
        panel = (
            <>
                <PanelHeader eyebrow={section.eyebrow} title={section.title ?? ""} intro={section.intro} logo={logo} />
                <FeatureSectionRows section={section} />
            </>
        );
    }

    return (
        // Phone: the tab strip stacks above the panel. In one row the panel
        // (flex 1, minWidth 0) collapsed to nothing beside the full-width strip,
        // so no section rendered on a phone (Bill, 2026-09-08).
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "flex-start" },
                minHeight: "100vh",
            }}
        >
            <Rail title="Our Capabilities" entries={entries} active={current.id} onSelect={select} />

            {/* Same page frame as BasePage (left-anchored, capped at 1280). */}
            <Box
                id="capabilities-panel"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 1280,
                    px: { xs: 3, md: 4 },
                    pt: { xs: 3, md: 6 },
                    pb: { xs: 4, md: 6 },
                    scrollMarginTop: 24,
                }}
            >
                {panel}
                {rest.length > 0 && (
                    <Stack spacing={4} sx={{ mt: 5 }}>
                        {rest.map((w) => (
                            <WidgetRenderer key={w.id} widget={w} room={room} />
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}

// The panel's title row: eyebrow, title at the page-H1 size, supplier logo
// on the right (the room's brand cue, as on every other page), intro under.
function PanelHeader({
    eyebrow,
    title,
    intro,
    logo,
}: {
    eyebrow?: string;
    title: string;
    intro?: string;
    logo?: ReactNode;
}) {
    return (
        <Box sx={{ mb: 4, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0 }}>
                {eyebrow && (
                    <Typography variant="overline" component="p" sx={{ mb: 0.5 }}>
                        {eyebrow}
                    </Typography>
                )}
                <Typography variant="h1" sx={{ ...typeScale.pageTitle, mt: 0, mb: intro ? 2 : 0 }}>
                    {title}
                </Typography>
                {intro && (
                    <Typography variant="body1" sx={{ color: "text.secondary", ...typeScale.lead, maxWidth: 1064 }}>
                        {intro}
                    </Typography>
                )}
            </Box>
            {logo}
        </Box>
    );
}

// Column icons: the drawer's MUI outlined set, keyed by the canonical section's
// `icon` name (unchanged in data since 2026-09-07) so no room or canonical
// record moves. Bill, 2026-09-08: the two nav levels share one icon library,
// and the two-tone Nucleo marks read too busy in a nav.
const RAIL_ICONS: Record<string, typeof InfoOutlined> = {
    about: InfoOutlined,
    marketplaceMgmt: StorefrontOutlined,
    directChannel: WebOutlined,
    whiteLabel: LabelOutlined,
    pricingBuyBox: SellOutlined,
    listingOpt: EditNoteOutlined,
    advertising: CampaignOutlined,
    handTruck: LocalShippingOutlined,
    customerService: SupportAgentOutlined,
    // Returns is a cycle, Customer support a life ring (Bill, 2026-09-08).
    cycle: AutorenewOutlined,
    lifeRing: SupportOutlined,
    integrations: HubOutlined,
};

function Rail({
    title,
    entries,
    active,
    onSelect,
}: {
    title: string;
    entries: Entry[];
    active: string;
    onSelect: (id: string) => void;
}) {
    const value = entries.findIndex((e) => e.id === active);
    const icon = (name: string | undefined, selected: boolean) => {
        const Glyph = name ? RAIL_ICONS[name] : undefined;
        return Glyph ? (
            <Glyph sx={{ fontSize: 20, flexShrink: 0, color: selected ? chrome.ink : chrome.iconPrimary }} />
        ) : null;
    };

    return (
        <>
            {/* Desktop: the second level of the room nav. Flush against the
                drawer, same white and right border, full window height,
                sticky, easing in from the drawer's edge on mount. */}
            <Box
                component="nav"
                aria-label={title}
                sx={{
                    display: { xs: "none", md: "flex" },
                    flexDirection: "column",
                    width: RAIL_WIDTH,
                    flexShrink: 0,
                    position: "sticky",
                    top: 0,
                    height: "100vh",
                    overflowY: "auto",
                    bgcolor: "#FFFFFF",
                    borderRight: `1px solid ${chrome.borderPrimary}`,
                    "@keyframes capabilities-rail-in": {
                        from: { transform: `translateX(-${RAIL_WIDTH / 4}px)`, opacity: 0 },
                        to: { transform: "translateX(0)", opacity: 1 },
                    },
                    animation: "capabilities-rail-in 240ms ease-out",
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                }}
            >
                <Typography
                    component="p"
                    sx={{
                        px: 2.5,
                        pt: 3,
                        pb: 1.5,
                        fontSize: 13,
                        lineHeight: "20px",
                        fontWeight: 600,
                        color: chrome.ink,
                    }}
                >
                    {title}
                </Typography>
                {entries.map((e, i) => {
                    const selected = i === value;
                    return (
                        <ButtonBase
                            key={e.id}
                            onClick={() => onSelect(e.id)}
                            aria-current={selected ? "true" : undefined}
                            sx={{
                                width: "100%",
                                justifyContent: "flex-start",
                                gap: 1.5,
                                px: 2.5,
                                py: 1.75,
                                borderTop: `1px solid ${colors.lineSoft}`,
                                bgcolor: selected ? chrome.surfaceActive : "transparent",
                                color: selected ? chrome.ink : chrome.inkBody,
                                // A <button> takes the browser's default face unless told
                                // otherwise; the drawer labels render through the theme.
                                fontFamily: (t) => t.typography.fontFamily,
                                fontSize: 13,
                                lineHeight: "20px",
                                fontWeight: 500,
                                textAlign: "left",
                                transition: "background-color 150ms ease",
                                "&:hover": { bgcolor: chrome.surfaceHover },
                            }}
                        >
                            {icon(e.icon, selected)}
                            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                                {e.label}
                            </Box>
                            <ChevronRightOutlined
                                sx={{ fontSize: 18, color: chrome.iconPrimary, flexShrink: 0 }}
                            />
                        </ButtonBase>
                    );
                })}
            </Box>

            {/* Phone: one horizontal scrollable line under the fixed top bar. */}
            <Box
                sx={{
                    display: { xs: "block", md: "none" },
                    position: "sticky",
                    top: SIDEBAR_MOBILE_BAR_HEIGHT,
                    zIndex: 2,
                    width: "100%",
                    bgcolor: "#FFFFFF",
                    borderBottom: `1px solid ${chrome.borderPrimary}`,
                }}
            >
                <Tabs
                    value={value < 0 ? false : value}
                    onChange={(_, v: number) => onSelect(entries[v].id)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    TabIndicatorProps={{ sx: { display: "none" } }}
                >
                    {entries.map((e) => (
                        <Tab
                            key={e.id}
                            disableRipple
                            label={e.label}
                            sx={{
                                minHeight: 48,
                                px: 2,
                                textTransform: "none",
                                fontSize: 13,
                                lineHeight: "20px",
                                fontWeight: 500,
                                color: chrome.inkBody,
                                opacity: 1,
                                "&.Mui-selected": { bgcolor: chrome.surfaceActive, color: chrome.ink },
                            }}
                        />
                    ))}
                </Tabs>
            </Box>
        </>
    );
}
